import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import { Button } from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import BillSummaryReportService from 'services/BillSummaryReportService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';
import { Grid, TextField, CircularProgress } from '@mui/material';

function StaffProductSalesReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });
  const [reportData, setReportData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredReportData, setFilteredReportData] = useState([]);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    staff_name: tCommon('Staff Name'),
    item_name: tCommon('Item Name'),
    total_quantity: tCommon('Total Quantity'),
    total_amount: {
      title: tCommon('Total Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.total_amount || 0);
      },
    },
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return filterFromDate !== prevFilterData.filterFromDate || filterToDate !== prevFilterData.filterToDate;
  }, [filterFromDate, filterToDate, prevFilterData.filterFromDate, prevFilterData.filterToDate]);

  useEffect(() => {
    if (ShopsHelper.isFreePlan(shop)) return;
    generateSummeryReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Staff Product Sales Report'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredReportData,
      {
        [tCommon('Total Quantity')]: summary.totalQuantity,
        [tCommon('Total Amount')]: `${shop?.currency || ''} ${summary.totalAmount}`,
      }
    );
  };

  const generateSummeryReport = () => {
    const { startDate, endDate } = dynamicDateRange('STAFF_PRODUCT_SALES_REPORT');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    loadStaffProductSalesReport({
      startTime: startDate,
      endTime: endDate,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
    });
  };

  const toCsvBtnPressed = async () => {
    const titleLine = tReport('Staff Product Sales Report for {{from}} to {{to}}', {
      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
      to: DateTimeHelpers.convertDateToDMY(filterToDate),
    });

    const csvWithoutTotal = CsvHelper.getString(filteredReportData, CSV_COLUMNS, titleLine);
    const summary = _getReportSummary(filteredReportData);
    const fields = Object.keys(CSV_COLUMNS);

    const totalRowValues = fields.map((key) => {
      switch (key) {
        case 'staff_name':
          return tCommon('Total');
        case 'total_quantity':
          return summary.totalQuantity;
        case 'total_amount':
          return summary.totalAmount;
        default:
          return '';
      }
    });

    const totalRow = totalRowValues.join(',') + '\n';
    const csv = csvWithoutTotal + '\n' + totalRow;

    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    saveAs(blob, `staff-product-sales-summary-${new Date().toISOString()}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const loadStaffProductSalesReport = async ({ startTime = filterFromDate, endTime = filterToDate } = {}) => {
    if (DateTimeHelpers.reportDateLimit(startTime, endTime)) {
      setDateLimitPopup(true);
      return;
    }
    setLoadingIndicator(true);
    try {
      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(startTime),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(endTime),
      };
      const resp = await BillSummaryReportService.getStaffProductSalesReport(data);
      const validData = resp.filter((item) => item.staff_name && item.staff_name.trim() !== '');
      setReportData(validData);
      setFilteredReportData(validData);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const searchLower = value.toLowerCase();
    setFilteredReportData(
      reportData.filter(
        (item) =>
          (item.staff_name && item.staff_name.toString().toLowerCase().includes(searchLower)) ||
          (item.item_name && item.item_name.toString().toLowerCase().includes(searchLower))
      )
    );
  };

  const headerData = [
    {
      label: tCommon('Staff Name'),
      id: 'staff_name',
      type: 'text',
    },
    {
      label: tCommon('Item Name'),
      id: 'item_name',
      type: 'text',
    },
    {
      label: tCommon('Total Quantity'),
      id: 'total_quantity',
      type: 'floatQuantity',
    },
    {
      label: tCommon('Total Amount'),
      id: 'total_amount',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.total_amount)}`;
      },
    },
  ];

  const _getReportSummary = (items) => {
    let totalQuantity = 0;
    let totalAmount = 0;
    const salesItems = items.filter((item) => item.item_name?.toLowerCase() !== 'discount');

    salesItems.forEach((item) => {
      totalQuantity += parseFloat(item.total_quantity || 0);
      totalAmount += parseFloat(item.total_amount || 0);
    });

    return {
      totalQuantity: totalQuantity,
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
    };
  };

  const summary = _getReportSummary(filteredReportData);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            {tCommon('CSV')}
          </Button>
        </div>
      </div>
      <div className={styles.changeable}>
        <Grid container spacing={1} alignItems="flex-end">
          {/* Report Title */}
          <Grid item xs={12}>
            <h2 className={styles.subTitle}>
              {tReport('Staff Product Sales Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </Grid>

          {/* Date Range */}
          <Grid item xs={12} md={3}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="STAFF_PRODUCT_SALES_REPORT"
            />
          </Grid>

          {/* Search */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={tCommon('Search by name')}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon />,
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              className={styles.greenBtn}
              onClick={generateSummeryReport}
              disabled={isShowGenerateButton === false || loadingIndicator}
              startIcon={loadingIndicator ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            >
              {tCommon('Generate Report')}
            </Button>
          </Grid>
        </Grid>
      </div>
      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : (
        <>
          {filteredReportData?.length > 0 ? (
            <div className={styles.tableContainer}>
              <Grayout open={isShowGenerateButton} />
              <DataTable
                columns={headerData}
                rows={filteredReportData}
                rowKey="staff_id-item_id"
                summary={{
                  staff_name: tCommon('Total'),
                  total_quantity: `${summary.totalQuantity}`,
                  total_amount: `${shop?.currency} ${summary.totalAmount}`,
                }}
              />
            </div>
          ) : (
            !loadingIndicator && (
              <Info
                severity="warning"
                title={tReport('Oh no, there are no sales matching to your filter')}
                content={tReport(
                  'There are no sales matching to your filter. Please verify your inputs to generate report.'
                )}
              />
            )
          )}
        </>
      )}
      {dateLimitPopup && (
        <DateLimitDialog
          handleClose={() => {
            setDateLimitPopup(false);
          }}
        />
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(StaffProductSalesReport);
